import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BreakingTicker from "../components/BreakingTicker";
import { business } from "../data";
import { FaNewspaper, FaAward, FaClock, FaUser, FaBitcoin, FaChartLine, FaGlobe, FaLandmark, FaYoutube, FaVideo, FaTwitter, FaFacebook, FaWhatsapp, FaLink, FaShare, FaSpinner } from "react-icons/fa";

const Breaking = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeNewsCategory, setActiveNewsCategory] = useState("pakistan");
    const [rssArticles, setRssArticles] = useState([]);
    const [newsLoading, setNewsLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Category Images and Metadata
    const categoryData = {
        pakistan: {
            name: "Pakistan",
            icon: "🇵🇰",
            banner: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=1200&h=400&fit=crop",
            color: "from-emerald-600 to-green-700",
            badge: "Local News",
            description: "Latest news from Pakistan, Punjab, and Okara"
        },
        international: {
            name: "International",
            icon: "🌍",
            banner: "https://images.unsplash.com/photo-1529429617124-95b109e86bb8?w=1200&h=400&fit=crop",
            color: "from-blue-600 to-blue-800",
            badge: "Global News",
            description: "World news and international affairs"
        },
        crypto: {
            name: "Crypto & Tech",
            icon: "₿",
            banner: "https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=1200&h=400&fit=crop",
            color: "from-purple-600 to-indigo-800",
            badge: "Digital & Tech",
            description: "Cryptocurrency, blockchain, and technology news"
        },
        economy: {
            name: "Economy & Business",
            icon: "📈",
            banner: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&h=400&fit=crop",
            color: "from-blue-700 to-cyan-800",
            badge: "Markets & Finance",
            description: "Stock markets, business, and economic updates"
        },
        sports: {
            name: "Sports",
            icon: "🏏",
            banner: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=1200&h=400&fit=crop",
            color: "from-orange-600 to-red-700",
            badge: "Sports News",
            description: "Latest sports updates and match coverage"
        }
    };

    // RSS Feed Sources
    const rssSources = {
        pakistan: {
            name: "Pakistan",
            icon: "🇵🇰",
            feeds: [
                { name: "The News International", url: "https://www.thenews.com.pk/rss/1/1" },
                { name: "Dawn News", url: "https://www.dawn.com/feeds/home" },
                { name: "Express Tribune", url: "https://tribune.com.pk/feed/" },
                { name: "Geo News", url: "https://www.geo.tv/rss/1/1" },
                { name: "ARY News", url: "https://arynews.tv/feed/" },
            ]
        },
        international: {
            name: "International",
            icon: "🌍",
            feeds: [
                { name: "BBC World", url: "http://feeds.bbci.co.uk/news/world/rss.xml" },
                { name: "Reuters", url: "https://feeds.reuters.com/Reuters/worldNews" },
                { name: "Al Jazeera", url: "https://www.aljazeera.com/xml/rss/all.xml" },
                { name: "CNN World", url: "http://rss.cnn.com/rss/edition_world.rss" },
                { name: "The Guardian", url: "https://www.theguardian.com/world/rss" },
            ]
        },
        crypto: {
            name: "Crypto & Tech",
            icon: "₿",
            feeds: [
                { name: "CoinDesk", url: "https://www.coindesk.com/arc/outboundfeeds/rss/" },
                { name: "CoinTelegraph", url: "https://cointelegraph.com/feed" },
                { name: "TechCrunch", url: "https://techcrunch.com/feed/" },
                { name: "Wired", url: "https://www.wired.com/feed/rss" },
                { name: "The Verge", url: "https://www.theverge.com/rss/index.xml" },
            ]
        },
        economy: {
            name: "Economy & Business",
            icon: "📈",
            feeds: [
                { name: "Reuters Business", url: "https://feeds.reuters.com/reuters/businessNews" },
                { name: "BBC Business", url: "http://feeds.bbci.co.uk/news/business/rss.xml" },
                { name: "Financial Times", url: "https://www.ft.com/?format=rss" },
                { name: "Bloomberg", url: "https://feeds.bloomberg.com/markets/news.rss" },
                { name: "Wall Street Journal", url: "https://feeds.a.dj.com/rss/RSSWorldNews.xml" },
            ]
        },
        sports: {
            name: "Sports",
            icon: "🏏",
            feeds: [
                { name: "ESPN", url: "https://www.espn.com/espn/rss/news" },
                { name: "BBC Sport", url: "http://feeds.bbci.co.uk/sport/rss.xml" },
                { name: "Sky Sports", url: "https://www.skysports.com/rss/12040" },
            ]
        }
    };

    // Fallback news data
    const getFallbackNews = (category) => {
        const fallbackData = {
            pakistan: [
                { title: "Pakistan's Economy Shows Signs of Recovery", description: "IMF approves $1.1 billion tranche for Pakistan", author: "Dawn News", time: new Date() },
                { title: "Punjab Government Announces New Development Package", description: "Chief Minister approves Rs. 50 billion for rural development", author: "The News", time: new Date(Date.now() - 3600000) },
                { title: "Okara Farmers Protest Against Water Shortage", description: "Farmers demand immediate release of water for crops", author: "Jang News", time: new Date(Date.now() - 7200000) },
                { title: "Weather Update: Rain Expected in Punjab", description: "Met office predicts rainfall in multiple cities", author: "Geo News", time: new Date(Date.now() - 10800000) },
            ],
            international: [
                { title: "US and China Resume Trade Talks", description: "Both nations agree to reduce tariffs on key goods", author: "Reuters", time: new Date() },
                { title: "UN Security Council Meeting on Gaza", description: "Emergency session called to address humanitarian crisis", author: "BBC", time: new Date(Date.now() - 3600000) },
                { title: "European Union Announces New Climate Package", description: "€500 billion allocated for green energy transition", author: "Al Jazeera", time: new Date(Date.now() - 7200000) },
            ],
            crypto: [
                { title: "Bitcoin Surges Past $70,000", description: "Market rally driven by institutional adoption", author: "CoinDesk", time: new Date() },
                { title: "Ethereum 2.0 Upgrade Complete", description: "Network transitions to Proof of Stake successfully", author: "CoinTelegraph", time: new Date(Date.now() - 3600000) },
                { title: "New AI Technology Disrupts Crypto Mining", description: "Revolutionary chip reduces energy consumption by 80%", author: "TechCrunch", time: new Date(Date.now() - 7200000) },
            ],
            economy: [
                { title: "Stock Markets Hit Record Highs", description: "S&P 500 and NASDAQ reach all-time highs", author: "Bloomberg", time: new Date() },
                { title: "Federal Reserve Holds Interest Rates Steady", description: "Inflation concerns keep rates unchanged", author: "Reuters", time: new Date(Date.now() - 3600000) },
                { title: "Global Oil Prices Drop 5%", description: "Supply concerns ease as production increases", author: "Financial Times", time: new Date(Date.now() - 7200000) },
            ],
            sports: [
                { title: "Pakistan Wins T20 Series Against South Africa", description: "Babar Azam leads team to historic victory", author: "ESPN", time: new Date() },
                { title: "PCB Announces New Domestic Cricket Structure", description: "Reforms aimed at improving player development", author: "BBC Sport", time: new Date(Date.now() - 3600000) },
                { title: "PSL 2026 Schedule Announced", description: "Matches to be played in 4 cities across Pakistan", author: "Sky Sports", time: new Date(Date.now() - 7200000) },
            ]
        };
        return fallbackData[category] || fallbackData.pakistan;
    };

    // Fetch RSS News
    const fetchRSSNews = async (rssUrl) => {
        try {
            const response = await fetch(
                `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === "ok" && data.items && data.items.length > 0) {
                return data.items.slice(0, 5).map((item) => ({
                    id: item.guid || Math.random().toString(),
                    title: item.title || "News Article",
                    description: item.description || "",
                    link: item.link || "#",
                    pubDate: item.pubDate || new Date().toISOString(),
                    author: item.author || "News Source",
                    thumbnail: item.thumbnail || item.enclosure?.link || "",
                    source: item.source || "News",
                    cleanDescription: item.description ? item.description.replace(/<[^>]*>/g, "").slice(0, 200) : "",
                }));
            }
            return [];
        } catch (error) {
            console.error("Error fetching RSS feed:", error);
            return [];
        }
    };

    // Fetch all RSS news
    const fetchAllRSSNews = async (category = "pakistan") => {
        setNewsLoading(true);
        try {
            const sources = rssSources[category]?.feeds || rssSources.pakistan.feeds;
            let allArticles = [];
            
            // Try to fetch from first 3 sources
            const sourcesToFetch = sources.slice(0, 3);
            
            for (const source of sourcesToFetch) {
                try {
                    const articles = await fetchRSSNews(source.url);
                    if (articles.length > 0) {
                        allArticles = [...allArticles, ...articles];
                    }
                } catch (e) {
                    console.error(`Error fetching from ${source.name}:`, e);
                }
            }
            
            // If no articles fetched, use fallback data
            if (allArticles.length === 0) {
                console.log("No RSS articles found, using fallback data");
                const fallbackArticles = getFallbackNews(category);
                allArticles = fallbackArticles.map((item) => ({
                    id: Math.random().toString(),
                    title: item.title,
                    description: item.description,
                    link: "#",
                    pubDate: item.time.toISOString(),
                    author: item.author,
                    cleanDescription: item.description,
                }));
            }
            
            // Sort by date
            allArticles.sort((a, b) => {
                return new Date(b.pubDate) - new Date(a.pubDate);
            });
            
            // Remove duplicates
            const uniqueArticles = allArticles.filter((article, index, self) =>
                index === self.findIndex((a) => a.title === article.title)
            );
            
            setRssArticles(uniqueArticles.slice(0, 12));
            setNewsLoading(false);
            setLoading(false);
            setError(null);
        } catch (error) {
            console.error("Error fetching RSS news:", error);
            // Use fallback data
            const fallbackArticles = getFallbackNews(category);
            const articles = fallbackArticles.map((item) => ({
                id: Math.random().toString(),
                title: item.title,
                description: item.description,
                link: "#",
                pubDate: item.time.toISOString(),
                author: item.author,
                cleanDescription: item.description,
            }));
            setRssArticles(articles);
            setNewsLoading(false);
            setLoading(false);
            setError("Using sample data. Please check your internet connection.");
        }
    };

    // Get time ago
    const getTimeAgo = (dateString) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
            if (diffInHours < 1) return "Just now";
            if (diffInHours < 24) return `${diffInHours} hours ago`;
            if (diffInHours < 48) return "1 day ago";
            if (diffInHours < 168) return `${Math.floor(diffInHours / 24)} days ago`;
            return `${Math.floor(diffInHours / 168)} weeks ago`;
        } catch {
            return "Recently";
        }
    };

    // Update current time
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // Load news on component mount
    useEffect(() => {
        fetchAllRSSNews("pakistan");
        
        const newsInterval = setInterval(() => fetchAllRSSNews(activeNewsCategory), 300000);
        
        return () => {
            clearInterval(newsInterval);
        };
    }, []);

    // Fetch news when category changes
    useEffect(() => {
        fetchAllRSSNews(activeNewsCategory);
    }, [activeNewsCategory]);

    const currentCategory = categoryData[activeNewsCategory] || categoryData.pakistan;

    return (
        <section className='pt-32 min-h-screen bg-gray-50'>
            <div className='container mx-auto px-4'>
                {/* Back Button */}
                <Link to="/" className='inline-flex items-center text-blue-600 hover:text-blue-800 transition mb-6 font-medium group'>
                    <span className="mr-2 group-hover:-translate-x-1 transition-transform">←</span>
                    Back to Home
                </Link>

                {/* Category Banner */}
                <div className="relative rounded-2xl overflow-hidden mb-8">
                    <div className={`bg-gradient-to-r ${currentCategory.color} h-48 md:h-64 flex items-center justify-center relative`}>
                        <img 
                            src={currentCategory.banner} 
                            alt={currentCategory.name}
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div className="relative z-10 text-center text-white">
                            <div className="text-6xl mb-3">{currentCategory.icon}</div>
                            <h1 className="text-4xl md:text-5xl font-bold">{currentCategory.name}</h1>
                            <p className="text-lg opacity-90 mt-2">{currentCategory.description}</p>
                            <div className="inline-block mt-3 bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-sm">
                                {currentCategory.badge} • Updated {currentTime.toLocaleTimeString()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Breaking Ticker */}
                <div className="mb-8">
                    <BreakingTicker />
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-center max-w-6xl mx-auto">
                        <p className="text-yellow-700 text-sm">{error}</p>
                    </div>
                )}

                {/* News Categories */}
                <div className="mb-8 max-w-6xl mx-auto">
                    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                        {Object.keys(rssSources).map((key) => (
                            <button
                                key={key}
                                onClick={() => setActiveNewsCategory(key)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-1 ${
                                    activeNewsCategory === key
                                        ? "bg-blue-900 text-white shadow-sm"
                                        : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                                }`}
                            >
                                <span>{rssSources[key].icon}</span>
                                {rssSources[key].name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* News Count and Stats */}
                <div className="flex flex-wrap items-center justify-between mb-6 max-w-6xl mx-auto">
                    <div className="flex items-center gap-3">
                        <FaNewspaper className="text-blue-600 text-xl" />
                        <h2 className="text-2xl font-bold text-blue-900">
                            {currentCategory.name} News
                        </h2>
                        <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                            {rssArticles.length} articles
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                        <FaClock className="w-4 h-4" />
                        <span>Last updated: {currentTime.toLocaleTimeString()}</span>
                    </div>
                </div>

                {/* Loading State */}
                {newsLoading && rssArticles.length === 0 && (
                    <div className="text-center py-12 max-w-6xl mx-auto">
                        <FaSpinner className="animate-spin text-blue-600 text-4xl mx-auto mb-4" />
                        <p className="text-gray-500">Loading news articles...</p>
                    </div>
                )}

                {/* RSS News Section - Grid Layout */}
                {!newsLoading && rssArticles.length > 0 ? (
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {rssArticles.map((article, index) => (
                                <a
                                    key={article.id || index}
                                    href={article.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden group"
                                >
                                    <div className="p-5">
                                        {/* Category Badge */}
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="text-sm">{currentCategory.icon}</span>
                                            <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                                {currentCategory.name}
                                            </span>
                                            <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
                                                <FaClock className="w-3 h-3" />
                                                {getTimeAgo(article.pubDate)}
                                            </span>
                                        </div>
                                        
                                        {/* Title */}
                                        <h3 className="text-base font-semibold text-gray-800 line-clamp-3 group-hover:text-blue-700 transition">
                                            {article.title}
                                        </h3>
                                        
                                        {/* Description */}
                                        {article.cleanDescription && (
                                            <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                                                {article.cleanDescription}
                                            </p>
                                        )}
                                        
                                        {/* Footer */}
                                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                                <FaUser className="w-3 h-3" />
                                                <span>{article.author || 'News Source'}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-blue-600 group-hover:text-blue-800">
                                                <span>Read More</span>
                                                <span className="group-hover:translate-x-1 transition">→</span>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                ) : (
                    !newsLoading && (
                        <div className="text-center py-12 max-w-6xl mx-auto">
                            <div className="text-6xl mb-4">📰</div>
                            <h3 className="text-xl font-semibold text-gray-700">No news articles found</h3>
                            <p className="text-gray-400 mt-2">Check back later for updates</p>
                        </div>
                    )
                )}

                {/* Share Section */}
                <div className="mt-12 max-w-6xl mx-auto">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FaShare className="text-blue-600" />
                            Share This Page
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            <button className="inline-flex items-center gap-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white px-4 py-2 rounded-full text-sm font-medium transition">
                                <FaTwitter /> Twitter
                            </button>
                            <button className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#1664d9] text-white px-4 py-2 rounded-full text-sm font-medium transition">
                                <FaFacebook /> Facebook
                            </button>
                            <button className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-4 py-2 rounded-full text-sm font-medium transition">
                                <FaWhatsapp /> WhatsApp
                            </button>
                            <button className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-full text-sm font-medium transition">
                                <FaLink /> Copy Link
                            </button>
                        </div>
                    </div>
                </div>

                {/* Why Kohla News Section */}
                <div className='mt-12 max-w-6xl mx-auto bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 md:p-10 text-white'>
                    <h2 className='text-2xl md:text-3xl font-bold text-center mb-6 flex items-center justify-center gap-3'>
                        <FaAward className="text-yellow-400" />
                        Hot News?
                    </h2>
                    <div className='grid md:grid-cols-3 gap-6'>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>⚡</div>
                            <h3 className='font-semibold text-lg'>Real-time Updates</h3>
                            <p className='text-blue-200 text-sm'>Instant news as it happens</p>
                        </div>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>📰</div>
                            <h3 className='font-semibold text-lg'>Trusted Sources</h3>
                            <p className='text-blue-200 text-sm'>Verified and reliable reporting</p>
                        </div>
                        <div className='text-center'>
                            <div className='text-4xl mb-3'>🎯</div>
                            <h3 className='font-semibold text-lg'>Diverse Coverage</h3>
                            <p className='text-blue-200 text-sm'>Local, national & international news</p>
                        </div>
                    </div>
                </div>

                {/* Follow Us CTA */}
                <div className='mt-10 text-center pb-6'>
                    <p className='text-gray-600 mb-4'>Stay informed with Kohla News</p>
                    <div className='flex flex-wrap justify-center gap-3'>
                        <a 
                            href={business.youtube}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-300 hover:shadow-lg text-sm'
                        >
                            <FaYoutube /> YouTube
                        </a>
                        <a 
                            href={business.facebook}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-300 hover:shadow-lg text-sm'
                        >
                            <FaFacebook /> Facebook
                        </a>
                        <a 
                            href={business.whatsappLink}
                            target='_blank'
                            rel='noreferrer'
                            className='inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-full font-semibold transition-all duration-300 hover:shadow-lg text-sm'
                        >
                            <FaWhatsapp /> WhatsApp
                        </a>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Breaking;